import { graphql } from 'babel-plugin-relay/macro'
import AppContext from 'context'
import dayjs from 'dayjs'
import { useContext, useMemo } from 'react'
import { useLazyLoadQuery } from 'react-relay/hooks'

import { AppOfflineLicenseTokenSectionQuery } from './__generated__/AppOfflineLicenseTokenSectionQuery.graphql'
import OfflineLicenseTokenManager from './OfflineLicenseTokenManager'

export const query = graphql`
  query AppOfflineLicenseTokenSectionQuery($appId: ID!) {
    viewer {
      # eslint-disable-next-line relay/unused-fields
      appById(id: $appId) {
        id
        licenses {
          id
          expiresAt
          token
        }
        offlineLicenseRequestStatus
        isOfflineLicenseEligible
      }
    }
  }
`

type Props = {
  appId: string
}

export function isOfflineLicenseAvailable(
  app: AppOfflineLicenseTokenSectionQuery['response']['viewer']['appById'],
  isSuperAdmin: boolean,
) {
  return (
    isSuperAdmin ||
    !!app?.isOfflineLicenseEligible ||
    !!app?.licenses.some((license) =>
      dayjs(license.expiresAt).isAfter(dayjs()),
    ) ||
    app?.offlineLicenseRequestStatus === 'REQUESTED'
  )
}

/** Section used to manage the offline license tokens for an app. Allows regular users
 * to see the existent license tokens and admins to generate new tokens.
 */
const AppOfflineLicenseTokenSection = ({ appId }: Props) => {
  const { isSuperAdmin } = useContext(AppContext)
  const { viewer } = useLazyLoadQuery<AppOfflineLicenseTokenSectionQuery>(
    query,
    {
      appId,
    },
  )

  const allLicenses = viewer.appById?.licenses || []
  const appIsOfflineLicenseEligible =
    viewer.appById?.isOfflineLicenseEligible ?? false

  const nonExpiredLicenses = useMemo(
    () =>
      allLicenses
        .filter((license) => dayjs(license.expiresAt).isAfter(dayjs()))
        .sort(
          (license1, license2) =>
            dayjs(license2.expiresAt).unix() - dayjs(license1.expiresAt).unix(),
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allLicenses.length],
  )

  const hasRequestedOfflineLicense =
    viewer.appById?.offlineLicenseRequestStatus === 'REQUESTED'

  // NOTE: We are going to deprecate offline only licenses. Until this feature is
  // fully deprecated, only super admins, apps of an organization who were granted
  // eligibility, customers who have a non-expired license(s), or those with
  // active requests, will be able to see this section
  const shouldRender = isOfflineLicenseAvailable(viewer.appById, !!isSuperAdmin)

  if (shouldRender) {
    return (
      <OfflineLicenseTokenManager
        appId={appId}
        appIsOfflineLicenseEligible={appIsOfflineLicenseEligible}
        licenses={nonExpiredLicenses}
        hasRequestedOfflineLicense={hasRequestedOfflineLicense}
      />
    )
  }

  return null
}

export default AppOfflineLicenseTokenSection
